<!-- Roles & Permissions Management -->
<div class="roles-management-container">
  <!-- Page Header -->
  <div class="page-header">
    <div class="header-content">
      <div class="header-main">
        <div class="header-icon">
          <i class="fas fa-shield-alt"></i>
        </div>
        <div class="header-text">
          <h1 class="page-title">Roles & Permissions</h1>
          <p class="page-description">Manage user roles and their permissions across the system</p>
        </div>
      </div>
      <div class="header-actions">
        <button class="btn btn-outline-secondary" (click)="getRoles()" [disabled]="isLoadingRoles">
          <i class="fas fa-sync-alt me-1" [class.fa-spin]="isLoadingRoles"></i>
          Refresh
        </button>
        <button class="btn btn-primary" (click)="createRole()">
          <i class="fas fa-plus me-1"></i>
          Create Role
        </button>
      </div>
    </div>
  </div>

  <!-- Search / Filter Panel -->
  <div class="filters-panel">
    <div class="search-section">
      <div class="search-box">
        <div class="search-input-wrapper">
          <i class="fas fa-search search-icon"></i>
          <input
            type="text"
            class="search-input"
            placeholder="Search roles by name or description..."
            (input)="searchRoles($event)"
            [(ngModel)]="searchTerm">
          <button class="clear-search" *ngIf="searchTerm" (click)="searchTerm = ''; searchRoles({target: {value: ''}})">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
    </div>
    <div class="stats-section">
      <div class="stat-card">
        <div class="stat-icon total">
          <i class="fas fa-users-cog"></i>
        </div>
        <div>
          <div class="stat-number">{{ filteredRoles.length }}</div>
          <div class="stat-label">Total Roles</div>
        </div>
      </div>
    </div>
  </div>

  <!-- Roles Table -->
  <div class="data-panel">
    <div class="panel-header">
      <div class="panel-title">
        <div class="title-icon">
          <i class="fas fa-users-cog"></i>
        </div>
        <div>
          <div class="title-text">System Roles</div>
          <div class="title-subtitle">Manage and configure user roles and their permissions</div>
        </div>
      </div>
    </div>

    <div class="panel-body">
      <div class="table-container">
        <table class="table">
          <thead>
            <tr>
              <th class="ps-4">Role Name</th>
              <th>Description</th>
              <th>Permissions</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <!-- Data Rows -->
            <tr *ngFor="let item of filteredRoles" [hidden]="isLoadingRoles">
              <td class="ps-4">
                <div class="d-flex align-items-center gap-3">
                  <div style="width: 34px; height: 34px; border-radius: var(--radius-sm); background: var(--accent-subtle); color: var(--accent); display: flex; align-items: center; justify-content: center; font-size: 13px; flex-shrink: 0;">
                    <i class="fas fa-user-tag"></i>
                  </div>
                  <div>
                    <div class="fw-semibold" style="font-size: 13px;">{{ item?.name }}</div>
                    <div style="font-size: 11px; color: var(--text-tertiary);">ID: #{{ item?.id }}</div>
                  </div>
                </div>
              </td>
              <td>
                <span *ngIf="item?.description; else noDescription" style="color: var(--text-secondary); font-size: 13px;">
                  {{ item?.description }}
                </span>
                <ng-template #noDescription>
                  <span style="color: var(--text-tertiary); font-size: 12px; font-style: italic;">No description provided</span>
                </ng-template>
              </td>
              <td>
                <span class="badge badge-soft-primary">
                  <i class="fas fa-key me-1"></i>
                  {{ item?.permissions?.length || 0 }} permission(s)
                </span>
              </td>
              <td>
                <div class="d-flex gap-2">
                  <button class="btn btn-sm btn-secondary"
                          (click)="edit(content, item)"
                          title="Edit Permissions">
                    <i class="fas fa-cog me-1"></i>
                    Permissions
                  </button>
                  <button class="btn btn-sm btn-outline-secondary"
                          style="color: var(--danger-text); border-color: var(--danger-border);"
                          (click)="delete(item.id)"
                          [disabled]="isDeletingRole"
                          title="Delete Role">
                    <i class="fas fa-trash me-1" *ngIf="!isDeletingRole"></i>
                    <i class="fas fa-spinner fa-spin me-1" *ngIf="isDeletingRole"></i>
                    Delete
                  </button>
                </div>
              </td>
            </tr>

            <!-- Loading Shimmer Rows -->
            <tr *ngFor="let i of [1,2,3,4,5]" [hidden]="!isLoadingRoles">
              <td class="ps-4">
                <div class="d-flex align-items-center gap-3">
                  <div class="loading-shimmer" style="width: 34px; height: 34px; border-radius: var(--radius-sm);"></div>
                  <div>
                    <div class="loading-shimmer" style="width: 120px; height: 14px; margin-bottom: 6px; border-radius: 4px;"></div>
                    <div class="loading-shimmer" style="width: 70px; height: 10px; border-radius: 4px;"></div>
                  </div>
                </div>
              </td>
              <td>
                <div class="loading-shimmer" style="width: 200px; height: 14px; border-radius: 4px;"></div>
              </td>
              <td>
                <div class="loading-shimmer" style="width: 110px; height: 24px; border-radius: 20px;"></div>
              </td>
              <td>
                <div class="d-flex gap-2">
                  <div class="loading-shimmer" style="width: 100px; height: 32px; border-radius: var(--radius-md);"></div>
                  <div class="loading-shimmer" style="width: 70px; height: 32px; border-radius: var(--radius-md);"></div>
                </div>
              </td>
            </tr>

            <!-- Empty State -->
            <tr *ngIf="filteredRoles?.length === 0 && !isLoadingRoles">
              <td colspan="4">
                <div class="empty-state">
                  <div class="empty-icon">
                    <i class="fas fa-shield-alt fa-3x"></i>
                  </div>
                  <h5 class="empty-title">No roles found</h5>
                  <p class="empty-subtitle" *ngIf="searchTerm">No roles match your search criteria. Try adjusting your search terms.</p>
                  <p class="empty-subtitle" *ngIf="!searchTerm">Get started by creating your first role to manage user permissions.</p>
                  <button class="btn btn-primary" (click)="createRole()" *ngIf="!searchTerm">
                    <i class="fas fa-plus me-1"></i>Create First Role
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</div>

<!-- Edit Role Modal -->
<ng-template #content let-modal>
  <div class="modal-header">
    <div class="d-flex align-items-center gap-3">
      <div style="width: 36px; height: 36px; border-radius: var(--radius-sm); background: var(--accent-subtle); color: var(--accent); display: flex; align-items: center; justify-content: center; font-size: 14px;">
        <i class="fas fa-shield-alt"></i>
      </div>
      <div>
        <h4 class="modal-title" style="margin: 0;">Edit Role & Permissions</h4>
        <p style="margin: 2px 0 0; font-size: 12px; color: var(--text-tertiary);">{{ name }}</p>
      </div>
    </div>
    <button type="button" class="btn-close" (click)="modal.close('close click')"></button>
  </div>

  <div class="modal-body">
    <!-- Role Information -->
    <div style="margin-bottom: 24px;">
      <h6 style="font-size: 13px; font-weight: 600; color: var(--text-primary); margin-bottom: 16px; display: flex; align-items: center; gap: 8px;">
        <i class="fas fa-user-tag" style="color: var(--accent);"></i>
        Role Information
      </h6>
      <div class="form-group">
        <label class="form-label">
          Role Name
        </label>
        <input type="text"
               class="form-control"
               name="name"
               [(ngModel)]="name"
               placeholder="Enter role name">
      </div>
      <div class="form-group">
        <label class="form-label">
          Description
        </label>
        <textarea class="form-control"
                  name="description"
                  [(ngModel)]="description"
                  rows="3"
                  placeholder="Enter role description (optional)"></textarea>
      </div>
    </div>

    <!-- Permissions -->
    <div>
      <div class="d-flex align-items-center justify-content-between" style="margin-bottom: 16px;">
        <h6 style="font-size: 13px; font-weight: 600; color: var(--text-primary); margin: 0; display: flex; align-items: center; gap: 8px;">
          <i class="fas fa-key" style="color: var(--accent);"></i>
          Permissions
        </h6>
        <span class="badge badge-soft-primary">{{ permissions?.length || 0 }} available</span>
      </div>

      <!-- Permissions List -->
      <div *ngIf="!isLoadingPermissions">
        <div style="display: grid; gap: 8px;">
          <div *ngFor="let perm of permissions; trackBy: trackByPermissionId"
               style="display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; background: var(--bg-muted); border-radius: var(--radius-md); border: 1px solid var(--border-subtle);">
            <div class="d-flex align-items-center gap-3">
              <div style="width: 28px; height: 28px; border-radius: var(--radius-sm); background: var(--accent-subtle); color: var(--accent); display: flex; align-items: center; justify-content: center; font-size: 11px;">
                <i class="fas fa-shield-alt"></i>
              </div>
              <div>
                <div style="font-size: 13px; font-weight: 500; color: var(--text-primary);">{{ formatPermission(perm?.name) }}</div>
                <div style="font-size: 11px; color: var(--text-tertiary);">{{ perm?.name }}</div>
              </div>
            </div>
            <div class="form-check form-switch">
              <input class="form-check-input"
                     type="checkbox"
                     role="switch"
                     [id]="'perm-' + perm?.id"
                     [checked]="isChecked(perm?.id)"
                     (change)="onCheckboxChange($event, perm?.name)">
              <label class="form-check-label" [for]="'perm-' + perm?.id"></label>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading Shimmer for Permissions -->
      <div *ngIf="isLoadingPermissions">
        <div style="display: grid; gap: 8px;">
          <div *ngFor="let i of [1,2,3,4,5]"
               style="display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; background: var(--bg-muted); border-radius: var(--radius-md);">
            <div class="d-flex align-items-center gap-3">
              <div class="loading-shimmer" style="width: 28px; height: 28px; border-radius: var(--radius-sm);"></div>
              <div>
                <div class="loading-shimmer" style="width: 150px; height: 14px; margin-bottom: 4px; border-radius: 4px;"></div>
                <div class="loading-shimmer" style="width: 100px; height: 10px; border-radius: 4px;"></div>
              </div>
            </div>
            <div class="loading-shimmer" style="width: 44px; height: 24px; border-radius: 12px;"></div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="modal-footer">
    <button class="btn btn-outline-secondary"
            (click)="modal.close('cancel')"
            [disabled]="isUpdatingRole">
      Cancel
    </button>
    <button class="btn btn-primary"
            type="button"
            (click)="update()"
            [disabled]="isUpdatingRole">
      <span *ngIf="!isUpdatingRole">
        <i class="fas fa-save me-1"></i>
        Update Permissions
      </span>
      <span *ngIf="isUpdatingRole">
        <i class="fas fa-spinner fa-spin me-1"></i>
        Updating...
      </span>
    </button>
  </div>
</ng-template>
